import { ArrowRightIcon, DollarSignIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'

import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { FormRadioGroupDS } from 'components/reusable/form/FormFields'
import { CenteredLayout } from 'pages/employer_onboarding/CenteredLayout'
import { useQuote } from 'pages/employer_onboarding/QuoteProvider'
import { useAPIErrorBanner } from 'pages/employer_onboarding/useErrorBanner'
import { emitEmployerOnboardingEvent, parseNumber } from 'pages/employer_onboarding/utils'
import {
  Button,
  Checkbox,
  CheckboxGroup,
  NumberMask,
  TextInput,
  usePageTitle
} from 'slab'
import {
  usePostQuotingProspectsProspectIdQuotesQuoteIdBenchmarks as useCreateBenchmarks,
  usePutQuotingProspectsId,
} from 'utils/web_api'
import { PutQuotingProspectsIdReasonsForChangingInsuranceItem, type QuotingProspect, type QuotingQuote } from 'utils/web_api.schemas'

import { usePrefill } from '../../PrefillProvider'
import { useProspect } from '../ProspectProvider'

function getDefaultValuesFromQuote (
  quote: QuotingQuote | null,
  prospect: QuotingProspect | null,
  prefillData?: {
    target_individual_deductible?: number
    target_individual_oop_max?: number
    target_network_type?: string
    target_metal_tier?: string
  }
) {
  return {
    target_individual_deductible: quote?.target_individual_deductible || prefillData?.target_individual_deductible,
    target_individual_oop_max: quote?.target_individual_oop_max || prefillData?.target_individual_oop_max,
    target_network_type: quote?.target_network_type || prefillData?.target_network_type,
    target_metal_tier: quote?.target_metal_tier || prefillData?.target_metal_tier,
    coverage_start_date: quote?.coverage_start_date,
    year: quote?.year,
    reasons: {
      make_employees_happier: prospect?.reasons_for_changing_insurance?.includes(PutQuotingProspectsIdReasonsForChangingInsuranceItem.make_employees_happier),
      avoid_rate_increases: prospect?.reasons_for_changing_insurance?.includes(PutQuotingProspectsIdReasonsForChangingInsuranceItem.avoid_rate_increases),
      best_benefits: prospect?.reasons_for_changing_insurance?.includes(PutQuotingProspectsIdReasonsForChangingInsuranceItem.best_benefits),
      visibility_control_of_healthcare_costs: prospect?.reasons_for_changing_insurance?.includes(PutQuotingProspectsIdReasonsForChangingInsuranceItem.visibility_control_of_healthcare_costs),
    },
  }
}

export function CurrentBenefitsPage () {
  usePageTitle('Current benefits')

  const { quote } = useQuote()
  const { prospect } = useProspect()
  const { getPrefillValue } = usePrefill()
  const { errorBanner, setError } = useAPIErrorBanner({
    fallbackMessage: 'Could not update your current benefits because of an error',
  })
  const { navigateToPreviousStep, navigateToNextStep } = useOnboardingStep()

  const createBenchmarksMutation = useCreateBenchmarks({
    mutation: {
      onError: setError,
    },
  })

  const updateProspectMutation = usePutQuotingProspectsId({
    mutation: {
      onSuccess: () => {
        navigateToNextStep()
      },
      onError: setError,
    },
  })

  const prefillData = getPrefillValue('benefits')

  const form = useForm({
    defaultValues: getDefaultValuesFromQuote(quote, prospect, prefillData),
    mode: 'onTouched',
  })

  const handleSubmit = () => {
    if (!quote) return

    const data = form.getValues()

    const reasons: PutQuotingProspectsIdReasonsForChangingInsuranceItem[] = []
    if (data.reasons.make_employees_happier) reasons.push(PutQuotingProspectsIdReasonsForChangingInsuranceItem.make_employees_happier)
    if (data.reasons.avoid_rate_increases) reasons.push(PutQuotingProspectsIdReasonsForChangingInsuranceItem.avoid_rate_increases)
    if (data.reasons.best_benefits) reasons.push(PutQuotingProspectsIdReasonsForChangingInsuranceItem.best_benefits)
    if (data.reasons.visibility_control_of_healthcare_costs) reasons.push(PutQuotingProspectsIdReasonsForChangingInsuranceItem.visibility_control_of_healthcare_costs)

    emitEmployerOnboardingEvent('[ER] Current benefits info added', {
      network_type: data.target_network_type,
      metal_tier: data.target_metal_tier,
      deductible: data.target_individual_deductible,
      oop_max: data.target_individual_oop_max,
      reason: reasons
    })

    createBenchmarksMutation.mutate({
      prospectId: quote.quoting_prospect_id,
      quoteId: quote.id,
      data: {
        target_individual_deductible: data.target_individual_deductible || 0,
        target_individual_oop_max: data.target_individual_oop_max || 0,
        target_network_type: data.target_network_type,
        target_metal_tier: data.target_metal_tier,
        coverage_start_date: quote.coverage_start_date!,
        year: new Date(quote.coverage_start_date!).getFullYear().toString(),
      },
    }, {
      onSuccess: () => {
        updateProspectMutation.mutate({
          id: quote.quoting_prospect_id,
          data: { reasons_for_changing_insurance: reasons },
        })
      },
    })
  }

  return (
    <CenteredLayout displaySupportLink>
      <CenteredLayout.Header
        heading='Great, tell us more!'
        subheading="We'll use this as a starting point to create your new Thatch allowances. If you offer multiple plans, choose the one the majority of your employees use."
      />
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <CenteredLayout.Container>
          <div>
            <h2 className='body mb-1'>
              Tell us a bit about your current benefits.
            </h2>
            <p className='body-sm text-gray-500'>
              If you offer multiple plans, choose the one that the majority of
              employees are on.
            </p>
          </div>
          <FormRadioGroupDS
            control={form.control}
            name='target_network_type'
            label='Network type'
            layout='horizontal'
            buttonVariant='bordered'
            buttonSize='small'
            options={[
              { label: 'PPO', value: 'ppo' },
              { label: 'HMO', value: 'hmo' },
              { label: 'POS', value: 'pos' },
              { label: 'EPO', value: 'epo' },
            ]}
          />
          <FormRadioGroupDS
            control={form.control}
            name='target_metal_tier'
            label='Tier'
            layout='horizontal'
            buttonVariant='bordered'
            buttonSize='small'
            options={[
              { label: 'Bronze', value: 'bronze' },
              { label: 'Silver', value: 'silver' },
              { label: 'Gold', value: 'gold' },
              { label: 'Platinum', value: 'platinum' },
            ]}
          />
          <div className='grid xs:grid-cols-2 gap-4'>
            <NumberMask decimalPlaces={0}>
              <TextInput
                {...form.register('target_individual_deductible', {
                  required: 'Deductible is required',
                  setValueAs: parseNumber,
                })}
                error={
                form.formState.errors.target_individual_deductible?.message
              }
                label='Deductible'
                leadingIcon={DollarSignIcon}
              />
            </NumberMask>
            <NumberMask decimalPlaces={0}>
              <TextInput
                {...form.register('target_individual_oop_max', {
                  required: 'Out-of-pocket max is required',
                  setValueAs: parseNumber,
                })}
                error={form.formState.errors.target_individual_oop_max?.message}
                label='Out-of-pocket max'
                leadingIcon={DollarSignIcon}
              />
            </NumberMask>
          </div>

          <CheckboxGroup label='What’s making you want to change?'>
            <Checkbox
              {...form.register('reasons.make_employees_happier')}
              label='Making sure my employees are happy'
            />
            <Checkbox
              {...form.register('reasons.avoid_rate_increases')}
              label='Avoiding unexpected rate increases'
            />
            <Checkbox
              {...form.register('reasons.best_benefits')}
              label='Providing the best benefits that I can afford'
            />
            <Checkbox
              {...form.register('reasons.visibility_control_of_healthcare_costs')}
              label='Visibility and control over healthcare costs'
            />
          </CheckboxGroup>
          {errorBanner}
        </CenteredLayout.Container>
        <CenteredLayout.Buttons>
          <Button variant='secondary' onClick={navigateToPreviousStep}>
            Back
          </Button>
          <Button
            variant='primary'
            disabled={createBenchmarksMutation.isPending || updateProspectMutation.isPending}
            type='submit'
            rightIcon={ArrowRightIcon}
          >
            Next
          </Button>
        </CenteredLayout.Buttons>
      </form>
    </CenteredLayout>
  )
}
